import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Bell, BellOff, Star, Droplets, Save } from 'lucide-react';
import { toast } from 'sonner';
import { playNotification, playSoundIfEnabled } from '../utils/sounds';
import { DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_FACES = [
  { value: 1, face: '😢', label: 'Kämpigt' },
  { value: 2, face: '😐', label: 'Okej' },
  { value: 3, face: '😊', label: 'Fint' },
  { value: 4, face: '😄', label: 'Jätteglad' },
];

// Generate 30-min time slots 5:00 AM - 11:00 PM
function generateTimeSlots() {
  const slots = [];
  for (let h = 5; h <= 22; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
    slots.push(`${String(h).padStart(2, '0')}:30`);
  }
  slots.push('23:00');
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

export default function DailyPlanner({ date: externalDate, compact = false, refreshKey = 0 }) {
  const [date, setDate] = useState(externalDate || new Date().toISOString().split('T')[0]);

  // Sync with external date prop when it changes
  useEffect(() => {
    if (externalDate && externalDate !== date) setDate(externalDate);
  }, [externalDate]);  

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const alarmsRef = useRef({});

  const fetchPlanner = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/daily-planner/${date}`, { withCredentials: true });
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchPlanner(); }, [fetchPlanner]);

  // Alarm checker - runs every 30 seconds
  useEffect(() => {
    if (!data?.alarms?.length) return;
    const check = () => {
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${now.getMinutes() < 30 ? '00' : '30'}`;
      const exactMin = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      data.alarms.forEach(alarmTime => {
        if ((currentTime === alarmTime || exactMin === alarmTime) && !alarmsRef.current[alarmTime + date]) {
          alarmsRef.current[alarmTime + date] = true;
          const blockText = data.time_blocks?.[alarmTime] || '';
          playSoundIfEnabled(playNotification);
          toast.info(`🔔 ${alarmTime} — ${blockText || 'Dags att börja!'}`, { duration: 10000 });
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(`Focus Critter – ${alarmTime}`, {
              body: blockText || 'Kolla in din dagsplanering!',
              tag: `alarm-${alarmTime}`,
            });
          }
        }
      });
    };
    const interval = setInterval(check, 30000);
    check();
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    return () => clearInterval(interval);
  }, [data?.alarms, data?.time_blocks, date]);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await axios.post(`${API}/daily-planner`, { ...data, date }, { withCredentials: true });
      toast.success('Planering sparad 🌸');
    } catch (e) { toast.error('Kunde inte spara'); }
    finally { setSaving(false); }
  };

  const update = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ---- Green-lane tasks auto-sync into Top Priorities ----
  const [greenTasks, setGreenTasks] = useState([]);

  const fetchGreenTasks = useCallback(async () => {
    try {
      const { data: tasks } = await axios.get(`${API}/tasks`, { withCredentials: true });
      const open = (tasks || [])
        .filter((t) => (t.lane || 'today') === 'today' && !t.completed)
        .slice(0, 3);
      setGreenTasks(open);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchGreenTasks(); }, [fetchGreenTasks, refreshKey]);

  // Re-fetch tasks when window regains focus (e.g. after editing in trafikljus)
  useEffect(() => {
    const onFocus = () => fetchGreenTasks();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchGreenTasks]);

  const toggleGreenTask = async (task) => {
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { completed: !task.completed }, { withCredentials: true });
      toast.success(task.completed ? 'Återställd' : `+${task.points || 15}p! 💛`);
      fetchGreenTasks();
    } catch { toast.error('Kunde inte uppdatera'); }
  };

  const addGreenTask = async (title) => {
    const trimmed = (title || '').trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API}/tasks`, {
        title: trimmed, lane: 'today', points: 15, priority: 'high',
      }, { withCredentials: true });
      fetchGreenTasks();
    } catch { toast.error('Kunde inte lägga till'); }
  };

  const updateGreenTaskTitle = async (task, newTitle) => {
    const trimmed = (newTitle || '').trim();
    if (!trimmed || trimmed === task.title) return;
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { title: trimmed }, { withCredentials: true });
      fetchGreenTasks();
    } catch { /* ignore */ }
  };

  const updatePriority = (idx, field, value) => {
    const arr = [...(data.top_priorities || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    update('top_priorities', arr);
  };

  const updateRoutine = (idx, field, value) => {
    const arr = [...(data.routines || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    update('routines', arr);
  };

  const updateGratitude = (idx, value) => {
    const arr = [...(data.gratitude || [])];
    arr[idx] = value;
    update('gratitude', arr);
  };

  const updateTimeBlock = (time, value) => {
    update('time_blocks', { ...(data.time_blocks || {}), [time]: value });
  };

  // ---- Schedule template (daily repeating tidsblock) ----
  const [tplBlocks, setTplBlocks] = useState({});         // {"07:00": "Vakna", ...}
  const [tplCompletions, setTplCompletions] = useState({}); // {"07:00": {done, skipped}}

  const fetchTemplate = useCallback(async () => {
    try {
      const { data: t } = await axios.get(`${API}/schedule-template`, {
        params: { date },
        withCredentials: true,
      });
      setTplBlocks(t.blocks || {});
      setTplCompletions(t.completions || {});
    } catch { /* ignore */ }
  }, [date]);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  const saveTemplateBlock = async (time, text) => {
    try {
      const { data: t } = await axios.post(`${API}/schedule-template/block`,
        { time, text: text || '' },
        { withCredentials: true });
      setTplBlocks(t.blocks || {});
    } catch { toast.error('Kunde inte spara tid'); }
  };

  const toggleTemplateDone = async (time, currentlyDone) => {
    try {
      await axios.post(`${API}/schedule-template/toggle`,
        { time, date, done: !currentlyDone },
        { withCredentials: true });
      setTplCompletions((prev) => ({
        ...prev,
        [time]: { ...(prev[time] || {}), done: !currentlyDone, skipped: false },
      }));
      if (!currentlyDone) toast.success('Bockad ✓', { duration: 1500 });
    } catch { toast.error('Kunde inte uppdatera'); }
  };

  // ---- Routines template (daily repeating rutiner) ----
  const [routineItems, setRoutineItems] = useState([]);
  const [routineCompletions, setRoutineCompletions] = useState({});
  const [newRoutineText, setNewRoutineText] = useState('');

  const fetchRoutines = useCallback(async () => {
    try {
      const { data: r } = await axios.get(`${API}/routines-template`, {
        params: { date },
        withCredentials: true,
      });
      setRoutineItems(r.items || []);
      setRoutineCompletions(r.completions || {});
    } catch { /* ignore */ }
  }, [date]);

  useEffect(() => { fetchRoutines(); }, [fetchRoutines]);

  const addRoutine = async () => {
    const text = (newRoutineText || '').trim();
    if (!text) return;
    try {
      const { data: r } = await axios.post(`${API}/routines-template/item`,
        { text }, { withCredentials: true });
      setRoutineItems(r.items || []);
      setNewRoutineText('');
    } catch { toast.error('Kunde inte lägga till'); }
  };

  const updateRoutineText = async (id, newText) => {
    const trimmed = (newText || '').trim();
    if (!trimmed) { removeRoutine(id); return; }
    try {
      const { data: r } = await axios.patch(`${API}/routines-template/item/${id}`,
        { text: trimmed }, { withCredentials: true });
      setRoutineItems(r.items || []);
    } catch { /* ignore */ }
  };

  const removeRoutine = async (id) => {
    try {
      const { data: r } = await axios.delete(`${API}/routines-template/item/${id}`,
        { withCredentials: true });
      setRoutineItems(r.items || []);
      setRoutineCompletions((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch { /* ignore */ }
  };

  const toggleRoutine = async (id, currentlyDone) => {
    try {
      await axios.post(`${API}/routines-template/toggle`,
        { item_id: id, date, done: !currentlyDone },
        { withCredentials: true });
      setRoutineCompletions((prev) => ({
        ...prev,
        [id]: { done: !currentlyDone },
      }));
      if (!currentlyDone) toast.success('Rutin klar ✓', { duration: 1500 });
    } catch { toast.error('Kunde inte uppdatera'); }
  };

  const toggleAlarm = (time) => {
    const alarms = [...(data.alarms || [])];
    if (alarms.includes(time)) {
      update('alarms', alarms.filter(a => a !== time));
    } else {
      update('alarms', [...alarms, time]);
    }
  };

  const changeDate = (delta) => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().split('T')[0]);
  };

  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const isToday = date === new Date().toISOString().split('T')[0];

  if (loading || !data) return <div className="neo-card p-8 text-center animate-pulse-soft">Loading planner...</div>;

  // Split time slots into two columns
  const midIdx = Math.ceil(TIME_SLOTS.length / 2);
  const col1 = TIME_SLOTS.slice(0, midIdx);
  const col2 = TIME_SLOTS.slice(midIdx);

  return (
    <div className="space-y-4" data-testid="daily-planner-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => changeDate(-1)} className="neo-btn p-2 bg-white dark:bg-[#252545]" data-testid="planner-prev"><ChevronLeft className="w-5 h-5" /></button>
        <div className="text-center">
          <h2 className="text-2xl font-bold font-['Fredoka'] italic">Dagsplanering</h2>
          <p className={`text-sm ${isToday ? 'text-blue-600 font-bold' : 'text-gray-500'}`}>{formatDate(date)}</p>
          <DoodleSquiggle width={80} color="#FFC8DD" className="mx-auto mt-1" />
        </div>
        <button onClick={() => changeDate(1)} className="neo-btn p-2 bg-white dark:bg-[#252545]" data-testid="planner-next"><ChevronRight className="w-5 h-5" /></button>
      </div>

      {/* Save button */}
      <button onClick={save} disabled={saving} className="neo-btn w-full py-2 bg-[#B9FBC0] flex items-center justify-center gap-2" data-testid="planner-save">
        <Save className="w-4 h-4" /> {saving ? 'Sparar…' : 'Spara planering'}
      </button>

      {/* Dagens mat — prominent full-width card */}
      <div className="neo-card p-4 bg-gradient-to-br from-[#FFF3F9] via-[#FFEDF5] to-[#FFF8E7]" data-testid="planner-meals-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-bold font-['Fredoka'] text-lg flex items-center gap-2">
            🍱 Dagens mat
          </h3>
          <p className="text-[11px] opacity-60 italic">Sparas automatiskt</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {[
            { key: 'breakfast', emoji: '🥞', label: 'Frukost', color: '#FFE4B5' },
            { key: 'lunch', emoji: '🥗', label: 'Lunch', color: '#B9F5D8' },
            { key: 'dinner', emoji: '🍝', label: 'Middag', color: '#FFD6E8' },
            { key: 'snack', emoji: '🍪', label: 'Mellanmål', color: '#E8D5FF' },
          ].map((meal) => (
            <label key={meal.key} className="neo-card p-2.5 bg-white flex flex-col gap-1" style={{ borderLeftWidth: '6px', borderLeftColor: meal.color }}>
              <span className="text-[11px] font-bold font-['Fredoka'] flex items-center gap-1">
                <span className="text-base">{meal.emoji}</span> {meal.label}
              </span>
              <input
                type="text"
                value={(data.meals || {})[meal.key] || ''}
                onChange={(e) => update('meals', { ...data.meals, [meal.key]: e.target.value })}
                onBlur={save}
                placeholder="Skriv vad du åt…"
                className="bg-transparent border-b border-dotted border-gray-300 px-1 py-1 text-sm focus:outline-none focus:border-black"
                data-testid={`meal-${meal.key}`}
              />
            </label>
          ))}
        </div>
      </div>

      {/* Main Grid: Left column + Right column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{
        backgroundImage: 'radial-gradient(circle, #e0e0e0 0.5px, transparent 0.5px)',
        backgroundSize: '16px 16px', padding: '8px', borderRadius: '1.5rem'
      }}>
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          {/* Top Priorities — synkade live från gröna trafikljus-tasks 🟢 */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]">
            <h3 className="font-bold font-['Fredoka'] text-base mb-1 text-[#FFC8DD] flex items-center gap-1.5">
              🟢 Topp-prioriteringar
            </h3>
            <p className="text-[10px] text-gray-400 mb-2 italic">Synkas automatiskt med trafikljus-gröna</p>
            {[0, 1, 2].map((i) => {
              const task = greenTasks[i];
              if (task) {
                return (
                  <div key={task.id} className="flex items-center gap-2 mb-2" data-testid={`priority-row-${i}`}>
                    <button
                      onClick={() => toggleGreenTask(task)}
                      className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-xs ${task.completed ? 'bg-[#B9FBC0] border-black' : 'bg-white border-gray-300'}`}
                      data-testid={`priority-check-${i}`}
                      title="Bockar av i båda listorna"
                    >
                      {task.completed ? '✓' : ''}
                    </button>
                    <input
                      type="text"
                      defaultValue={task.title || ''}
                      onBlur={(e) => updateGreenTaskTitle(task, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      className="flex-1 bg-[#FFC8DD20] border-b-2 border-[#FFC8DD] px-2 py-1 text-sm focus:outline-none rounded-sm"
                      data-testid={`priority-input-${i}`}
                    />
                  </div>
                );
              }
              return (
                <div key={i} className="flex items-center gap-2 mb-2" data-testid={`priority-row-${i}`}>
                  <span className="w-6 h-6 rounded-full border-2 border-dashed border-gray-300 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder={`Skriv prio ${i + 1} och tryck Enter…`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addGreenTask(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                    className="flex-1 bg-[#FFC8DD10] border-b-2 border-dashed border-[#FFC8DD] px-2 py-1 text-sm focus:outline-none rounded-sm italic"
                    data-testid={`priority-input-${i}`}
                  />
                </div>
              );
            })}
            {greenTasks.length === 0 && (
              <p className="text-[10px] text-gray-400 italic mt-1">Inga gröna tasks än — skriv något ovan, eller lägg till i trafikljusen nedan 🟢</p>
            )}
          </div>

          {/* To-Do List (from existing tasks) */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]">
            <h3 className="font-bold font-['Fredoka'] text-base mb-2 text-[#A2D2FF]">Att göra-lista</h3>
            <p className="text-xs text-gray-500">Scrolla ner till trafikljusen 🟢🟡🔴 nedan för att hantera tasks</p>
          </div>

          {/* Routines — daglig mall med kryssrutor */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]" data-testid="planner-routines">
            <h3 className="font-bold font-['Fredoka'] text-base mb-1 text-[#A2D2FF] flex items-center gap-1.5">
              🔁 Rutiner
            </h3>
            <p className="text-[10px] text-gray-400 mb-2 italic">Gäller varje dag · bockas av per dag</p>
            {routineItems.map((r) => {
              const done = !!routineCompletions[r.id]?.done;
              return (
                <div key={r.id} className="flex items-center gap-2 mb-2 group" data-testid={`routine-row-${r.id}`}>
                  <button
                    onClick={() => toggleRoutine(r.id, done)}
                    className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-xs ${done ? 'bg-[#B9FBC0] border-black' : 'bg-white border-gray-300'}`}
                    data-testid={`routine-check-${r.id}`}
                  >
                    {done ? '✓' : ''}
                  </button>
                  <input
                    type="text"
                    defaultValue={r.text}
                    onBlur={(e) => { if (e.target.value.trim() !== r.text) updateRoutineText(r.id, e.target.value); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    className={`flex-1 bg-[#A2D2FF20] border-b-2 border-[#A2D2FF] px-2 py-1 text-sm focus:outline-none rounded-sm ${done ? 'line-through opacity-60' : ''}`}
                    data-testid={`routine-input-${r.id}`}
                  />
                  <button
                    onClick={() => removeRoutine(r.id)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 text-lg px-1"
                    title="Ta bort rutin"
                    data-testid={`routine-delete-${r.id}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-gray-200">
              <span className="w-6 h-6 rounded-full border-2 border-dashed border-gray-300 flex-shrink-0" />
              <input
                type="text"
                value={newRoutineText}
                onChange={(e) => setNewRoutineText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addRoutine(); }}
                onBlur={addRoutine}
                placeholder="Lägg till ny rutin + Enter…"
                className="flex-1 bg-[#A2D2FF10] border-b-2 border-dashed border-[#A2D2FF] px-2 py-1 text-sm focus:outline-none rounded-sm italic"
                data-testid="routine-input-new"
              />
            </div>
            {routineItems.length === 0 && (
              <p className="text-[10px] text-gray-400 italic mt-1">Inga rutiner än — skriv ovan 🌸</p>
            )}
          </div>
        </div>

        {/* MIDDLE COLUMN */}
        <div className="space-y-4">
          {/* Mood */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]">
            <h3 className="font-bold font-['Fredoka'] text-base mb-2 text-[#FFC8DD]">Humör</h3>
            <div className="flex justify-center gap-3">
              {MOOD_FACES.map(m => (
                <button key={m.value} onClick={() => update('mood', data.mood === m.value ? null : m.value)}
                  className={`w-10 h-10 rounded-full border-2 text-xl transition-all ${data.mood === m.value ? 'border-black scale-110 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'border-gray-300 opacity-60'}`}
                  data-testid={`planner-mood-${m.value}`}>{m.face}</button>
              ))}
            </div>
          </div>

          {/* Water Balance */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]">
            <h3 className="font-bold font-['Fredoka'] text-base mb-2 text-[#A2D2FF]">Vattenbalans</h3>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 8 }).map((_, i) => (
                <button key={i} onClick={() => update('water', data.water === i + 1 ? i : i + 1)}
                  className={`w-8 h-10 rounded-lg border-2 flex items-center justify-center transition-all ${i < data.water ? 'border-blue-400 bg-blue-100' : 'border-gray-300 bg-white'}`}
                  data-testid={`water-${i}`}>
                  <Droplets className={`w-4 h-4 ${i < data.water ? 'text-blue-500' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-gray-400 mt-1">{data.water}/8 glas</p>
          </div>

          {/* Meals (legacy quick-view inside middle column — kept for compatibility but hidden visually since main card above) */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545] hidden">
            <h3 className="font-bold font-['Fredoka'] text-base mb-2 text-[#FFC8DD]">Måltider</h3>
            {['breakfast', 'lunch', 'dinner'].map((meal, i) => (
              <div key={meal} className="flex items-center gap-2 mb-2">
                <span className="w-4 text-xs font-bold text-gray-400">{meal[0].toUpperCase()}</span>
                <input type="text" value={(data.meals || {})[meal] || ''} onChange={(e) => update('meals', { ...data.meals, [meal]: e.target.value })}
                  className="flex-1 bg-[#A2D2FF15] border-b border-dotted border-gray-300 px-2 py-1 text-sm focus:outline-none" placeholder={meal}
                  data-testid={`meal-legacy-${meal}`} />
              </div>
            ))}
          </div>

          {/* Notes */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]">
            <h3 className="font-bold font-['Fredoka'] text-base mb-2 text-[#CDB4DB]">Anteckningar</h3>
            <textarea value={data.notes || ''} onChange={(e) => update('notes', e.target.value)}
              className="w-full bg-[#CDB4DB10] border border-dotted border-gray-300 rounded-lg p-2 text-sm focus:outline-none resize-none" rows={3} placeholder="Dagens tankar…"
              data-testid="planner-notes" />
          </div>

          {/* Thanks For */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]">
            <h3 className="font-bold font-['Fredoka'] text-base mb-2 text-[#FFC8DD]">Tacksamt för</h3>
            {(data.gratitude || []).map((g, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-gray-400 italic w-3">{i + 1}</span>
                <input type="text" value={g || ''} onChange={(e) => updateGratitude(i, e.target.value)}
                  className="flex-1 bg-transparent border-b border-dotted border-gray-300 px-1 py-1 text-sm focus:outline-none" placeholder="Jag är tacksam för…"
                  data-testid={`gratitude-${i}`} />
              </div>
            ))}
          </div>

          {/* Productivity */}
          <div className="neo-card p-4 bg-white dark:bg-[#252545]">
            <h3 className="font-bold font-['Fredoka'] text-base mb-2 text-[#FDF0D5]">Produktivitet</h3>
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map(s => (
                <button key={s} onClick={() => update('productivity', data.productivity === s ? 0 : s)}
                  data-testid={`productivity-star-${s}`}>
                  <Star className={`w-7 h-7 transition-all ${s <= data.productivity ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Time Blocking (daglig mall som låser appen) */}
        <div className="neo-card p-4 bg-white dark:bg-[#252545]">
          <h3 className="font-bold font-['Fredoka'] text-base mb-1 text-center text-[#FFC8DD]">Tidsblock</h3>
          <p className="text-[10px] text-center text-gray-400 italic mb-1">
            Gäller varje dag 🔒 Låser appen tills du bockar av
          </p>
          <p className="text-[10px] text-center text-[#D06892] mb-2">
            💡 Tryck på en rad → skriv vad du vill göra → påminnelse + ljud när tiden kommer 🔔
          </p>
          <div className="grid grid-cols-2 gap-x-3">
            {[col1, col2].map((col, colIdx) => (
              <div key={colIdx} className="space-y-1">
                {col.map(time => {
                  const nextH = parseInt(time.split(':')[0]);
                  const nextM = parseInt(time.split(':')[1]) + 30;
                  const endTime = nextM >= 60 ? `${String(nextH + 1).padStart(2, '0')}:00` : `${String(nextH).padStart(2, '0')}:${String(nextM).padStart(2, '0')}`;
                  const tplText = tplBlocks[time] || '';
                  const comp = tplCompletions[time] || {};
                  const isDone = !!comp.done;
                  const isSkipped = !!comp.skipped;
                  const isPast = isToday && time < new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                  const isOverdue = isPast && tplText && !isDone && !isSkipped;

                  return (
                    <div
                      key={time}
                      className={`flex items-center gap-1 ${isPast && !isOverdue ? 'opacity-50' : ''} ${isOverdue ? 'ring-2 ring-[#FFC8DD] rounded' : ''}`}
                      data-testid={`time-row-${time.replace(':', '')}`}
                    >
                      {tplText ? (
                        <button
                          onClick={() => toggleTemplateDone(time, isDone)}
                          className={`flex-shrink-0 w-5 h-5 rounded-full border-2 border-black flex items-center justify-center text-[10px] ${isDone ? 'bg-[#B9FBC0]' : isSkipped ? 'bg-gray-200' : 'bg-white'}`}
                          title={isDone ? 'Bockad — klicka för att återställa' : 'Bocka av'}
                          data-testid={`time-check-${time.replace(':', '')}`}
                        >
                          {isDone ? '✓' : isSkipped ? '⋯' : ''}
                        </button>
                      ) : (
                        <span className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-dashed border-[#FFC8DD]/60" />
                      )}
                      <div className="flex-shrink-0 w-[64px] bg-[#FFC8DD20] rounded-md px-1 py-0.5">
                        <span className="text-[10px] font-mono text-gray-600 dark:text-gray-300 italic">{time}</span>
                      </div>
                      <input
                        type="text"
                        defaultValue={tplText}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== tplText) saveTemplateBlock(time, v);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        className={`flex-1 min-w-0 border-2 rounded-md px-1.5 py-1 text-xs focus:outline-none transition-colors ${
                          isDone
                            ? 'line-through opacity-60 bg-[#B9FBC020] border-[#B9FBC0]'
                            : tplText
                              ? 'bg-white border-[#A2D2FF] focus:border-[#D06892] focus:bg-[#FFF0F8]'
                              : 'bg-[#FFFAFC] border-dashed border-[#FFC8DD]/40 focus:bg-white focus:border-[#D06892] focus:border-solid'
                        }`}
                        placeholder={tplText ? '' : '+ Lägg till…'}
                        data-testid={`time-block-${time.replace(':', '')}`}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
